import { format, parse } from 'date-fns'
import { ServerResponse } from 'http'
import { ParsedUrlQuery } from 'querystring'
import { runContentfulExperimentOnServer } from '@/experimentation/server'
import { GiveawaySchedule, PublicTheatricalDiscount, ValidPublicDiscounts } from '@/services/Theatrical/queries'
import { GetTheatricalReleasePromosQuery } from '@/types/codegen-contentful'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import DavidKidsGoFreeBanner from '../../ShowtimesView/components/banners/DavidKidsGoFreeBanner'
import { DavidGiveawayBanner } from '../../ShowtimesView/components/banners/GiveawayBanners/DavidGiveawayBanner'
import DavidKidsGoFreeModal from '../../ShowtimesView/components/modals/DavidKidsGoFreeModal'
import { DavidGiveawayModal } from '../../ShowtimesView/components/modals/GiveawayModal/DavidGiveawayModal'
import { TheatricalPromotion, TheatricalPromotionType, PromoImage, IncomingMessageWithCookies } from './types'

type PromoItems = NonNullable<GetTheatricalReleasePromosQuery['theatricalReleasePromosCollection']>['items'][number]
type PromoItem = NonNullable<NonNullable<PromoItems>['itemsCollection']>['items'][number]
type PromoContent = Exclude<
  NonNullable<NonNullable<PromoItem>['contentCollection']>['items'][number],
  { __typename?: 'GrowthbookExperimentMappings' }
>

const isValidImage = (
  image: Maybe<{
    url?: string | null
    width?: number | null
    height?: number | null
  }>,
): image is PromoImage => {
  return !!image?.url && typeof image?.width === 'number' && typeof image?.height === 'number'
}

// In contentful, we select the promoType from a dropdown, but the type coming from Contentful is still just a string,
// so we need to explicity match it here to keep Typescript happy
const getValidPromoType = (promoType: string): TheatricalPromotionType | null => {
  if (promoType === 'early_bird') return 'early_bird'
  if (promoType === 'no_fees') return 'no_fees'
  if (promoType === 'premium_guild') return 'premium_guild'
  if (promoType === 'giveaway') return 'giveaway'
  if (promoType === 'claim-free-ticket') return 'claim-free-ticket'
  if (promoType === 'custom') return 'custom'
  return null
}

const resolveTheatricalPromoBannerOnServer = async (
  content: Maybe<PromoContent>,
): Promise<TheatricalPromotion['banner'] | null> => {
  const banner = content?.banner
  if (!banner) return null

  if (
    banner?.__typename === 'TheatricalReleasePromoImageBanner' &&
    isValidImage(banner.desktopImage) &&
    isValidImage(banner.tabletImage) &&
    isValidImage(banner.mobileImage)
  ) {
    return {
      bannerType: 'custom_image',
      desktopImage: banner.desktopImage,
      tabletImage: banner.tabletImage,
      mobileImage: banner.mobileImage,
    }
  }

  if (
    banner?.__typename === 'TheatricalReleasePromoEarlybirdBanner' &&
    isValidImage(banner.backgroundImage) &&
    isValidImage(banner.foregroundImage) &&
    banner.openingWeek &&
    banner.accentColor
  ) {
    return {
      bannerType: 'early_bird',
      backgroundImage: banner.backgroundImage,
      foregroundImage: banner.foregroundImage,
      accentColor: banner.accentColor,
      openingWeek: banner.openingWeek,
    }
  }

  if (
    banner?.__typename === 'TheatricalReleasePromoNofeesBanner' &&
    isValidImage(banner.backgroundImage) &&
    isValidImage(banner.foregroundImage) &&
    banner.accentColor
  ) {
    return {
      bannerType: 'no_fees',
      backgroundImage: banner.backgroundImage,
      foregroundImage: banner.foregroundImage,
      accentColor: banner.accentColor,
    }
  }

  if (banner?.__typename === 'TheatricalReleasePromoCustomCode' && isValidCustomCodeName(banner.componentMap)) {
    return {
      bannerType: 'custom_code',
      name: banner.componentMap,
    }
  }

  return null
}

const resolveTheatricalPromoModalOnServer = async (
  content: Maybe<PromoContent>,
): Promise<TheatricalPromotion['modal'] | null> => {
  const modal = content?.modal
  if (!modal) return null

  if (
    modal?.__typename === 'TheatricalReleasePromoEarlybirdModal' &&
    isValidImage(modal.backgroundImage) &&
    modal.openingWeek &&
    modal.accentColor &&
    modal.openingWeek
  ) {
    return {
      modalType: 'early_bird',
      backgroundImage: modal.backgroundImage,
      accentColor: modal.accentColor,
      openingWeek: modal.openingWeek,
    }
  }

  if (
    modal?.__typename === 'TheatricalReleasePromoNofeesModal' &&
    isValidImage(modal.backgroundImage) &&
    modal.accentColor
  ) {
    return {
      modalType: 'no_fees',
      backgroundImage: modal.backgroundImage,
      accentColor: modal.accentColor,
    }
  }

  if (modal?.__typename === 'TheatricalReleasePromoCustomCode' && isValidCustomCodeName(modal.componentMap)) {
    return {
      modalType: 'custom_code',
      name: modal.componentMap,
    }
  }

  return null
}

const resolveTheatricalPromoItemOnServer = async (
  item: Maybe<PromoItem>,
  req: IncomingMessageWithCookies,
  res: ServerResponse,
) => {
  const promoType = getValidPromoType(item?.promoType ?? '')
  if (!item?.code || !promoType) return null

  const content = await runContentfulExperimentOnServer(req, res, item?.contentCollection?.items ?? [])
  const banner = await resolveTheatricalPromoBannerOnServer(content)
  const modal = await resolveTheatricalPromoModalOnServer(content)

  const bonusStream =
    content?.bonusStream?.projectSlug && content?.bonusStream?.ticketsRequired && content?.bonusStream?.streamPath
      ? {
          slug: content.bonusStream.projectSlug,
          ticketsRequired: content.bonusStream.ticketsRequired,
          streamPath: content.bonusStream.streamPath,
        }
      : null

  return {
    code: item?.code,
    promoType,
    banner,
    modal,
    bonusStream,
  }
}

export const resolveTheatricalPromosOnServer = async (
  data: GetTheatricalReleasePromosQuery,
  req: IncomingMessageWithCookies,
  res: ServerResponse,
) => {
  const config = data.theatricalReleasePromosCollection?.items?.[0]

  if (!config?.theatricalSlug || !config?.itemsCollection?.items) return null

  const items = []
  for (const item of config?.itemsCollection?.items ?? []) {
    const resolvedItem = await resolveTheatricalPromoItemOnServer(item, req, res)
    if (resolvedItem) items.push(resolvedItem)
  }

  return {
    theatricalSlug: config.theatricalSlug,
    items,
  }
}

export type ResolvedTheatricalPromos = NonNullable<Awaited<ReturnType<typeof resolveTheatricalPromosOnServer>>>

const resolveBasePromo = (
  theatricalSlug: string,
  item: Awaited<ReturnType<typeof resolveTheatricalPromoItemOnServer>>,
): Omit<TheatricalPromotion, 'publicDiscount'> | null => {
  if (!item) return null

  // Check here to see if the item doesn't have any content, and if so, return null.
  // This would happen if an empty item was added as part of an experiment where
  // we want one variation not to show anything.
  if (!item.banner && !item.modal && !item.bonusStream) return null

  return {
    theatricalSlug,
    promoType: item.promoType,
    code: item.code,
    banner: item.banner,
    modal: item.modal,
    bonusStream: item.bonusStream ?? null,
  }
}

const getPromoFromId = async (
  theatricalSlug: string,
  promoItems: PromoItem[],
  id: string,
  req: IncomingMessageWithCookies,
  res: ServerResponse,
) => {
  const item = promoItems?.find((item) => item?.id?.toLowerCase() === id.toLowerCase()) ?? null
  if (!item) return null
  const resolvedItem = await resolveTheatricalPromoItemOnServer(item, req, res)
  return resolveBasePromo(theatricalSlug, resolvedItem)
}

const getPromoFromType = async (
  theatricalSlug: string,
  promoItems: PromoItem[],
  promoType: TheatricalPromotionType,
  req: IncomingMessageWithCookies,
  res: ServerResponse,
) => {
  const item = promoItems?.find((item) => item?.promoType === promoType) ?? null
  if (!item) return null
  const resolvedItem = await resolveTheatricalPromoItemOnServer(item, req, res)
  return resolveBasePromo(theatricalSlug, resolvedItem)
}

const getDefaultPromo = async (
  theatricalSlug: string,
  promoItems: PromoItem[],
  req: IncomingMessageWithCookies,
  res: ServerResponse,
) => {
  const item = promoItems?.find((item) => item?.isDefault) ?? null
  if (!item) return null
  const resolvedItem = await resolveTheatricalPromoItemOnServer(item, req, res)
  return resolveBasePromo(theatricalSlug, resolvedItem)
}

/* eslint-disable sonarjs/cognitive-complexity */
export const pickCurrentPromo = async ({
  promos,
  query,
  theatricalSlug,
  hasGuildTickets,
  publicDiscounts,
  giveawaySchedule,
  req,
  res,
}: {
  promos: GetTheatricalReleasePromosQuery | null
  query: ParsedUrlQuery
  theatricalSlug: string
  hasGuildTickets: boolean
  publicDiscounts: ValidPublicDiscounts
  giveawaySchedule: GiveawaySchedule | null
  req: IncomingMessageWithCookies
  res: ServerResponse
}): Promise<TheatricalPromotion | null> => {
  const promoItems = promos?.theatricalReleasePromosCollection?.items?.[0]?.itemsCollection?.items
  if (!promoItems) return null

  const promoQueryStr = typeof query.promo === 'string' ? query.promo : null

  // Eject early if promo is claim-free-ticket
  if (promoQueryStr === 'claim-free-ticket') {
    return {
      theatricalSlug,
      code: 'CLAIM_FREE_TICKET',
      promoType: 'claim-free-ticket',
      banner: null,
      modal: null,
      publicDiscount: null,
      bonusStream: null,
    }
  }

  // Prefer promo from query
  const queryPromo = promoQueryStr
    ? (await getPromoFromId(theatricalSlug, promoItems, promoQueryStr, req, res)) ?? null
    : null
  if (queryPromo) {
    return { ...queryPromo, promoType: 'custom', publicDiscount: null }
  }

  // Then early bird discount if we have one
  if (publicDiscounts.earlyBirdDiscount) {
    const earlyBirdPromo = await getPromoFromType(theatricalSlug, promoItems, 'early_bird', req, res)
    if (earlyBirdPromo)
      return {
        ...earlyBirdPromo,
        promoType: 'early_bird',
        publicDiscount: publicDiscounts.earlyBirdDiscount,
      }
  }

  // Then giveaway schedule if enabled
  if (giveawaySchedule) {
    const giveawayPromo = await getPromoFromType(theatricalSlug, promoItems, 'giveaway', req, res)
    if (giveawayPromo) return { ...giveawayPromo, promoType: 'giveaway', publicDiscount: null }
  }

  // Then guild discounts
  if (hasGuildTickets) {
    const premiumGuildPromo = await getPromoFromType(theatricalSlug, promoItems, 'premium_guild', req, res)
    if (premiumGuildPromo) return { ...premiumGuildPromo, promoType: 'premium_guild', publicDiscount: null }
    // If no custom guild promo, we have a defualt banner promo
    if (!premiumGuildPromo) {
      return {
        theatricalSlug,
        code: 'PREMIUM_GUILD',
        promoType: 'premium_guild',
        banner: {
          bannerType: 'premium_guild',
        },
        modal: null,
        publicDiscount: null,
        bonusStream: null,
      }
    }
  }

  // Then find a valid default promo
  // In practice, this should either be NOFEES or some custom promo
  const defaultPromo = await getDefaultPromo(theatricalSlug, promoItems, req, res)
  if (defaultPromo) {
    const isNoFees = defaultPromo.promoType === 'no_fees' || defaultPromo.banner?.bannerType === 'no_fees'
    if (isNoFees && publicDiscounts.noFeesDiscount) {
      return {
        ...defaultPromo,
        promoType: 'no_fees',
        publicDiscount: publicDiscounts.noFeesDiscount,
      }
    }

    if (defaultPromo.promoType === 'custom') {
      return { ...defaultPromo, promoType: 'custom', publicDiscount: null }
    }
  }

  return null
  // TODO: where do limited-date promos fit in?
}

const customCodeMap: Record<string, JSX.Element | ((open: boolean, onClose: () => void) => JSX.Element)> = {
  DavidKidsGoFreeBanner: <DavidKidsGoFreeBanner />,
  KidsGoFreeModal: (open: boolean, onClose: () => void) => <DavidKidsGoFreeModal open={open} onClose={onClose} />,
  DavidGiveawayBanner: <DavidGiveawayBanner />,
  DavidGiveawayModal: (open: boolean, onClose: () => void) => <DavidGiveawayModal open={open} onClose={onClose} />,
}

const isValidCustomCodeName = (name: string | null | undefined): name is keyof typeof customCodeMap => {
  if (!name) return false
  return name in customCodeMap
}

export const getCustomCodeComponent = (name: string) => {
  return isValidCustomCodeName(name) ? customCodeMap[name] : null
}

export const useRestrictionCopy = (discount: PublicTheatricalDiscount, openingWeek?: string) => {
  const { t } = useTranslate('tickets')

  const isOffHoursOnly = discount.modifierFlags?.includes('OFF_HOURS_ONLY')
  const isOpeningWeekOffHoursOnly = discount.modifierFlags?.includes('OPENING_WEEK_OFF_HOURS_ONLY')
  const hasBlackouts =
    (discount.blackouts && discount.blackouts.length > 0) || discount.modifierFlags?.includes('BLACKOUTS')
  const dateRange = discount.showtimeValidDateRange

  let dateString = ''
  if (isOpeningWeekOffHoursOnly && openingWeek) {
    dateString = openingWeek
  } else if (dateRange && dateRange.lower && dateRange.upper) {
    const lowerDate = format(parse(dateRange.lower, 'yyyy-MM-dd', new Date()), 'MMM d')
    const upperDate = format(parse(dateRange.upper, 'yyyy-MM-dd', new Date()), 'MMM d')
    dateString = `${lowerDate} ${t('through', 'through')} ${upperDate}`
  }

  const timeString =
    isOffHoursOnly || isOpeningWeekOffHoursOnly
      ? t('monThruThursAndFriThruSunBefore5', 'Any showtime Mon thru Thurs, or Fri thru Sun before 5pm')
      : ''

  const blackoutString = hasBlackouts ? t('blackoutsMayApply', 'Blackout dates may apply') : ''

  const arr = [dateString, timeString, blackoutString].filter(Boolean)

  return arr.length > 0 ? arr : null
}
